import InputAdornment from '@mui/material/InputAdornment'
import THEME_COLORS from 'styles/colors.scss'
import { LocationOn as LocationOnIcon } from '@vedmarket/ui-kit';
import { CalendarToday as CalendarTodayIcon } from '@vedmarket/ui-kit';

const trimTrailingSlash = (url: string) => url.replace(/\/$/, '')

const PROD_API_URL = 'https://api.vedmarket.ru'
const isDevelopment = process.env.NODE_ENV === 'development'

/** Django API (заказы, таможня, справочники). Dev без .env → compose `app` на :8080 */
export const API_URL = process.env.REACT_APP_API_URL
  || (isDevelopment ? 'http://localhost:8080' : PROD_API_URL)

/**
 * Хост socket.io (path всегда /ws/socket.io).
 * Namespace product chat: /chat, support chat: /support-chat.
 * Prod: тот же домен, что API_URL. Локально с fastapi: http://localhost:8003
 */
export const SOCKET_API_URL = process.env.REACT_APP_SOCKET_API_URL
  || (isDevelopment ? 'http://localhost:8003' : API_URL)

/**
 * FastAPI HTTP API (история чата, presence).
 * Prod: шлюз с префиксом /al-assist. Локально с fastapi: http://localhost:8003 (без /al-assist)
 */
export const CHAT_API_URL = process.env.REACT_APP_CHAT_API_URL
  || (isDevelopment ? 'http://localhost:8003' : `${trimTrailingSlash(API_URL)}/al-assist`)

export const PRODUCT_CHAT_SOCKET_URL = `${trimTrailingSlash(SOCKET_API_URL)}/chat`
export const SUPPORT_CHAT_SOCKET_URL = `${trimTrailingSlash(SOCKET_API_URL)}/support-chat`

// false — не подключать socket.io: чат поддержки и AI-ассистент в товаре
export const SOCKET_IO_ENABLED = process.env.REACT_APP_SOCKET_IO_ENABLED !== 'false'

/** Временно: кнопка «Загрузить инвойс или упаковочный» в расчёте */
export const DOCUMENT_IMPORT_ENABLED = false

/**
 * Origin SPA frontend/company. На проде обычно тот же хост — пусто.
 * Локально: http://localhost:3001 (company dev-server), если calculator на :3000.
 */
export const COMPANY_APP_ORIGIN = process.env.REACT_APP_COMPANY_APP_URL
  ? trimTrailingSlash(process.env.REACT_APP_COMPANY_APP_URL)
  : ''

export const enum ROUTES {
  REGISTER = '/register',
  LOGIN = '/login',
  FORGOT_PASSWORD = '/forgot-password',
  RESET_PASSWORD = '/reset-password',
  WELCOME = '/welcome',
  DEMO = '/demo',
  CABINET = 'cabinet',
  CALCULATOR = 'calculator',
  CALCULATOR_VIEW = 'calculator/view',
  CALCULATIONS = 'calculations',
  APPLICATIONS = 'applications',
  /** Manager detail URL: /cabinet/applications/manager/:publicId */
  APPLICATIONS_MANAGER = 'manager',
  ORDERS = 'orders',
  SETTINGS = 'settings',
  SUPPORT = 'support',
  NOTIFICATIONS = 'notifications',
  DICTIONARIES = 'dictionaries',
  CURRENCY = 'currency',
  LOCATIONS = 'locations',
  COUNTRIES = 'contries',
  CITIES = 'cities',
  INCOTERMS = 'incoterms',
  TRANSPORT_COMPANIES = 'transport_companies',
  COMPANY = 'company',
  PALLETS = 'pallets',
  INFORMERS = 'informers',
  ROUTES = 'routes',
  ROUTES_FREIGHT = 'freights',
  ROUTES_LIST_ITEM = 'list/item',
  ROUTES_LIST_ITEM_NEW = 'list/item/new',
  ROUTES_FREIGHT_ITEM = 'freights/item',
  ROUTES_FREIGHT_ITEM_NEW = 'freights/item/new',
  TARIFFS = 'tariffs',
  SERVICES = 'services',
  CONDITIONS = 'conditions',
  LIST = 'list',
  NEW = 'new',
  ITEM = 'item',
  NOT_FOUND = '/not-found',
}

export const enum PAYER_TYPE {
  INDIVIDUAL = 'individual',
  LEGAL_ENTITY = 'legal_entity',
}

export const enum PACKAGING_METHOD {
  COMBINED_CARGO = 1,
  CONTAINERS = 2,
}

export const enum CERTIFICATION_DECLARATION_TYPE {
  BATCH = 'batch',
}

export const enum DEMO_CALCULATOR_STEP {
  MAIN = 'main',
  PACKAGING = 'packaging',
  DELIVERY = 'delivery',
  CUSTOMS = 'customs',
  CERTIFICATION = 'certification',
  BANKING_COMMISSIONS = 'banking_commissions',
  ADDITIONAL_SERVICES = 'additional_services',
}

export const enum DEMO_FIELD_TYPE {
  AUTOCOMPLETE = 'autocomplete',
  DATE = 'date',
  RADIO = 'radio',
  SELECT = 'select',
  TEXTFIELD = 'textfield',
}

export const enum DEMO_PLAYER_INPUT {
  QUANTITY = 'quantity',
  PRICE = 'price',
}

export const enum NOTIFICATION_SEVERITY {
  ERROR = 'error',
  INFO = 'info',
  SUCCESS = 'success',
  WARNING = 'warning',
}

export const enum NOTIFICATION_ACTION {
  READ = 'read',
  UNREAD = 'unread',
}

export const enum NOTIFICATION_TABLE_COLUMN {
  SECTION = 'section',
  MESSAGE = 'message',
  CREATED_AT = 'created_at',
  SETTINGS = 'settings',
}

export const DECLARATION_EXAMPLES_PAGE_SIZE = 3;

export const COLORS = {
  WARNING_ALERT_TEXT: THEME_COLORS.WARNING_ALERT_TEXT,
  ORANGE: THEME_COLORS.ORANGE,
  BLUE: THEME_COLORS.BLUE,
  BLUE_GRAY_600: THEME_COLORS.BLUE_GRAY_600,
  BLUE_DARK: THEME_COLORS.BLUE_DARK,
  DARK: THEME_COLORS.DARK,
  BEIGE: THEME_COLORS.BEIGE,
  GRAY: THEME_COLORS.GRAY,
  GRAY_200: THEME_COLORS.GRAY_200,
  DARK_GRAY: THEME_COLORS.DARK_GRAY,
  NEUTRAL_GRAY: THEME_COLORS.NEUTRAL_GRAY,
  LIGHT_GRAY: THEME_COLORS.LIGHT_GRAY,
  WHITE: THEME_COLORS.WHITE,
  GREEN: THEME_COLORS.GREEN,
  RED: THEME_COLORS.RED,
  LIME: THEME_COLORS.LIME,
  BORDER_GRAY: THEME_COLORS.BORDER_GRAY,
  STRONG_GRAY: THEME_COLORS.STRONG_GRAY,
  BLACK: THEME_COLORS.BLACK,
  BLACK_DARK: THEME_COLORS.BLACK_DARK,
  SLIGHTLY_GRAY: THEME_COLORS.SLIGHTLY_GRAY,
  LIGHT_TEXT_SECONDARY: THEME_COLORS.LIGHT_TEXT_SECONDARY,
  LIGHT_TEXT_DISABLED: THEME_COLORS.LIGHT_TEXT_DISABLED,
  LIGHT_TEXT_DISABLED_OPACITY: THEME_COLORS.LIGHT_TEXT_DISABLED_OPACITY,
  WARNING: THEME_COLORS.WARNING,
  LIGHT_BLUE: THEME_COLORS.LIGHT_BLUE,
  BLUE_FOCUS: THEME_COLORS.BLUE_FOCUS,
  MEDIUM_GRAY: THEME_COLORS.MEDIUM_GRAY,
  LIGHT_RED_OPACITY: THEME_COLORS.LIGHT_RED_OPACITY,
  LIGHT_BEIGE: THEME_COLORS.LIGHT_BEIGE,
  LIGHT_ACTION: THEME_COLORS.LIGHT_ACTION,
  GRAY_50: THEME_COLORS.GRAY_50,
  LIGHT_OUTLINED_BORDER: THEME_COLORS.LIGHT_OUTLINED_BORDER,
  INFO_ALERT_BACKGROUND: THEME_COLORS.INFO_ALERT_BACKGROUND,
  INFO_ALERT_ICON: THEME_COLORS.INFO_ALERT_ICON,
  INFO_ALERT_TEXT: THEME_COLORS.INFO_ALERT_TEXT,
  YELLOW: THEME_COLORS.YELLOW
}

export const ADORNMENTS = {
  AMOUNT: <InputAdornment position='end'>шт.</InputAdornment>,
  PRICE: <InputAdornment position='end'>USD</InputAdornment>,
  WEIGHT: <InputAdornment position='end'>кг</InputAdornment>,
  SIZE: <InputAdornment position='end'>см</InputAdornment>,
  SIZE_SMALL: <InputAdornment position='end'>мм</InputAdornment>,
  VOLUME: <InputAdornment position='end'>м<sup>3</sup></InputAdornment>,
  CALENDAR: <InputAdornment position='end'><CalendarTodayIcon/></InputAdornment>,
  PERCENT: <InputAdornment position='end'>%</InputAdornment>,
}

export const ADORNMENT_LOCATION = <InputAdornment position='start'>
  <LocationOnIcon style={{ color: COLORS.LIGHT_GRAY, marginLeft: '-2px', marginRight: '8px'}}/>
</InputAdornment>

export const MUI_LICENSE_KEY = '2eb6f9cda4e888996b232db3d18843e3Tz0xMjg0NjUsRT0xODA4NTI0Nzk5MDAwLFM9cHJvLExNPWFubnVhbCxQVj1RMy0yMDI0LEtWPTI='

export const enum INFORMERS_CODES {
  VOLUME_WEIGHT = 'volume_weight',
  TNVED_CODE = 'tnved_code',
  ORIGIN_CERTIFICATE = 'origin_certificate',
  EXCISE = 'excise',
  DUTY_TAX = 'duty_tax',
  DELIVERY_CITY = 'delivery_city',
  CUSTOMS_PAYMENT = 'customs_payment',
  CUSTOMS_COST = 'customs_cost',
  CONTAINER_CNT = 'container_cnt',
  INVOICE_NUM = 'invoice_num',
}

export const enum BITRIX_USER_STATUS {
  NEW = 'NEW',
  IN_PROCESS = 'IN_PROCESS',
  PROCESSED = 'PROCESSED',
  JUNK = 'JUNK',
  CONVERTED = 'CONVERTED'
}

export const IS_AGREED_DEMO = "isAgreedDemo";

export const enum SORTING_OPTIONS {
  CODE_DESC = 'code',
  CODE_ASC = '-code',
  NAME_DESC = 'name',
  NAME_ASC = '-name',
}

export const enum ACTIONS {
  EDIT = 'EDIT',
  REMOVE = 'REMOVE',
}

export const INITIAL_DATA_REQUEST = {
  limit: 50,
  page: 1,
  ordering: SORTING_OPTIONS.CODE_DESC
}


export enum APPLICATION_STATUS {
  PREPARING = 'preparing',
  AWAITING_CLIENT = 'awaiting_client',
  PREPARATION_COMPLETE = 'preparation_complete',
  AWAITING_EXECUTOR_PROPOSAL = 'awaiting_executor_proposal',
  PROPOSAL_READY = 'proposal_ready',
  AWAITING_EXECUTOR_CONTACT = 'awaiting_executor_contact',
  AT_EXECUTOR = 'at_executor',
  CLOSED = 'closed',
}


export enum APPLICATION_CHECK_STATUS {
  PENDING = 'pending',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
}


export enum APPLICATION_PROPOSAL_STATUS {
  DRAFT = 'draft',
  PUBLISHED = 'published',
  DECLINED = 'declined',
}


export enum APPLICATION_CLARIFICATION_STATUS {
  OPEN = 'open',
  ANSWERED = 'answered',
}


export enum APPLICATION_OUTCOME {
  UNKNOWN = 'unknown',
  CLIENT_REFUSED = 'client_refused',
}


export enum APPLICATION_EVENT_TYPE {
  PROPOSAL_DRAFTED = 'proposal_drafted',
  PROPOSAL_PUBLISHED = 'proposal_published',
  PROPOSAL_DECLINED = 'proposal_declined',
  CHECK_ITEM_UPDATED = 'check_item_updated',
  CLARIFICATION_REQUESTED = 'clarification_requested',
  CLARIFICATION_ANSWERED = 'clarification_answered',
}


export enum CHECK_BLOCK_VISUAL_STATE {
  COMPLETED = 'completed',
  NEEDS_DATA = 'needs-data',
  DEFAULT = 'default',
}


export enum MANAGER_WORKFLOW_STEP_STATE {
  COMPLETED = 'completed',
  CURRENT = 'current',
  UPCOMING = 'upcoming',
  SKIPPED = 'skipped',
}


export enum MANAGER_WORKFLOW_STEP_ID {
  PREPARATION = 'preparation',
  EXECUTOR = 'executor',
  CLIENT_PACKAGE = 'client_package',
  PROPOSAL = 'proposal',
  CLIENT_DECISION = 'client_decision',
  CONTACTS = 'contacts',
  OUTCOME = 'outcome',
}


export enum APPLICATION_LIST_TAB {
  MINE = 'mine',
  QUEUE = 'queue',
}


export enum APPLICATION_TIMELINE_ENTRY_KIND {
  CLARIFICATION = 'clarification',
  COMMENT = 'comment',
  EVENT = 'event',
}

export const APPLICATION_PROPOSAL_CURRENCY_OPTIONS = [ 'USD', 'RUB', 'EUR', 'CNY' ] as const

export enum APPLICATION_TABLE_COLUMN {
  ID = 'public_id',
  REQUEST = 'request',
  STATUS = 'status',
  CALCULATION = 'calculation',
  CLIENT = 'client',
  CREATED_AT = 'created_at',
  SETTINGS = 'settings',
}

export enum APPLICATION_SHIPPING_CODE {
  SEA = 'sea',
  AUTO = 'auto',
  AVIA = 'avia',
  RAILS = 'rails',
}

export const APPLICATION_DETAIL_VIEW_PARAM = 'view'
export const APPLICATION_DETAIL_MANAGER_VIEW = 'manager'

export enum DELIVERY_TIME_FILTER {
  ANY = 'any',
}

export enum DECLARATION_FORM {
  ORIGIN_CERTIFICATE = 'origin_certificate',
  HAS_PREFERENCES = 'has_preferences',
}

export const APPLICATION_DOCUMENT_ACCEPTED_FILES = {
  'application/pdf': [ '.pdf' ],
  'application/msword': [ '.doc' ],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [ '.docx' ],
  'application/vnd.ms-excel': [ '.xls' ],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [ '.xlsx' ],
}
